// Item mass, in kilograms — the number the inventory screen was missing.
//
// WHY THIS FILE EXISTS. The round-6 critique's finding was blunt: "zero item
// names, tooltips or weight figures anywhere in a screen holding 25 items". In
// DayZ you always know what a thing is, what condition tier it is in, and what
// carrying it costs you; a container that reports only a fill bar is reporting
// half of what a survival inventory is for. Names and condition were already
// there (see InventoryUI._tip). Mass was not, anywhere.
//
// WHY IT IS NOT IN src/game/items.js. It belongs there and it should move there
// the moment that catalog grows a `mass` field. It is not there today because
// src/game/ is outside this round's file ownership, and because a UI-side table
// that is honest about being UI-side is better than a screen that silently
// pretends mass does not exist. Nothing in gameplay reads these numbers — they
// are presentation only, so no balance can drift while they live here.
//
// The values are real-world masses of the object as carried, not of its
// contents: a full 1 L canteen is ~1.0 kg, a folded field jacket ~1.2 kg, a
// bandage 30 g. They are per UNIT — a stack of 4 blueberries weighs 4x the
// entry. Ordering matters more than any single figure: the screen has to make
// "the pack is what is slowing me down, not the pills" legible at a glance.
import { ITEMS } from '../game/items';

const MASS = {
  // ---- forage: light, and there is a lot of it ----
  mush_porcini: 0.05, mush_chanterelle: 0.04, mush_birch_bolete: 0.05,
  mush_puffball: 0.09, mush_morel: 0.04, mush_morel_roasted: 0.03,
  mush_fly_agaric: 0.05, mush_death_cap: 0.04, mush_false_morel: 0.04,
  mush_false_morel_roasted: 0.03,
  berry_blueberry: 0.02, berry_raspberry: 0.02, berry_lingonberry: 0.02,
  berry_elderberry: 0.02, berry_elderberry_cooked: 0.02, berry_rosehip: 0.02,
  berry_rowan: 0.02, berry_rowan_cooked: 0.02, fruit_apple: 0.15,
  // ---- meat: the heaviest food in the game, and roasting drives water off ----
  meat_raw_chicken: 0.3, meat_cooked_chicken: 0.24,
  meat_raw_mutton: 0.65, meat_cooked_mutton: 0.5,
  meat_raw_game: 0.7, meat_cooked_game: 0.55,
  // ---- tinned + packaged ----
  canned_stew: 0.42, canned_fish: 0.19, canned_veg: 0.4, canned_soup: 0.4,
  canned_fruit: 0.58, bulging_can: 0.42, candy_bar: 0.06, crackers: 0.22,
  // ---- drink ----
  soda_can: 0.36, juice_can: 0.36, vodka_bottle: 0.85,
  murky_water: 1.0, water_bottle: 0.95,
  // ---- medical / tools / valuables ----
  bandage: 0.03, duct_tape: 0.16, painkillers: 0.02, antibiotics: 0.03,
  rags: 0.12, purification_tablets: 0.02, matches: 0.02, cooking_stick: 0.2,
  ammo_pistol: 0.012, ammo_rifle: 0.024,
  cigarettes: 0.03, pocket_watch: 0.08, wallet: 0.1,
  // ---- worn ----
  tshirt: 0.2, flannel_shirt: 0.42, shorts: 0.3, work_pants: 0.8,
  rain_jacket: 0.65, field_jacket: 1.2,
  backpack_courier: 0.8, backpack_hiking: 1.6, backpack_expedition: 2.4,
};

// kg per grid cell, by kind — the fallback so a brand-new catalog entry still
// reports something with the right order of magnitude instead of 0.0
const PER_CELL = {
  food: 0.3, drink: 0.5, med: 0.05, tool: 0.2, valuable: 0.1,
  shirt: 0.08, jacket: 0.14, pants: 0.14, backpack: 0.1,
};

// mass of ONE unit of an item id
export function massOf(id) {
  if (MASS[id] != null) return MASS[id];
  const def = ITEMS[id];
  if (!def) return 0;
  return (PER_CELL[def.kind] ?? 0.15) * def.size[0] * def.size[1];
}

// mass of a stack instance ({ id, qty })
export const massOfStack = (it) => massOf(it.id) * (it.qty || 1);

// Total for a list of stacks. A worn container's own mass is NOT included here
// — the caller decides, because a jacket weighs its own 1.2 kg in the character
// total but its pockets' contents are what its own capacity readout is about.
export function massOfItems(items) {
  let kg = 0;
  for (const it of items || []) kg += massOfStack(it);
  return kg;
}

// One formatter for the whole UI so the grid, the captions and the tooltip can
// never disagree about rounding. Sub-kilo masses keep two decimals (a 30 g
// bandage reading "0.0 kg" is worse than no figure at all).
export function kg(v) {
  if (!(v > 0)) return '0 kg';
  if (v < 0.1) return v.toFixed(2).replace(/0$/, '') + ' kg';
  return (v < 10 ? v.toFixed(1) : Math.round(v)) + ' kg';
}
